export { Login }

import { useState } from 'react'
import type { FormEvent } from 'react'
import { clearWhenSettled } from '../../components/clearWhenSettled.ts'
import { describeUnexpectedError } from '../../components/describeError.ts'
import { Logo } from '../../components/Logo.tsx'
import { Footer } from '../../components/Footer.tsx'
import { onSendMagicLoginEmail } from './Login.telefunc.ts'
import { errorStyle, hintStyle, successStyle, linkStyle } from '../../components/messageStyles.ts'
import { primaryButtonStyle, inputStyle } from '../../components/formStyles.ts'
import { RATE_LIMITED_MESSAGE, SUPPORT_EMAIL } from '../../utils/messages.ts'

type Result =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent'; email: string }
  | { kind: 'unknown'; email: string }
  | { kind: 'rateLimited' }
  | { kind: 'error'; message: string }

function Login({ linkExpired: initialLinkExpired = false }: { linkExpired?: boolean }) {
  const [email, setEmail] = useState('')
  const [result, setResult] = useState<Result>({ kind: 'idle' })
  // Seeded from the loaded data (set when a just-consumed `#login:` token didn't sign us in);
  // cleared when the user starts editing the email field below.
  const [linkExpired, setLinkExpired] = useState(initialLinkExpired)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (result.kind === 'submitting') return
    // Freeze the submitted email so the result message doesn't follow live edits.
    const submittedEmail = email
    setResult({ kind: 'submitting' })
    try {
      const res = await onSendMagicLoginEmail(submittedEmail)
      if (res.kind === 'sent') setResult({ kind: 'sent', email: submittedEmail })
      else if (res.kind === 'unknown') setResult({ kind: 'unknown', email: submittedEmail })
      else setResult({ kind: 'rateLimited' })
    } catch (err) {
      console.error('onSendMagicLoginEmail failed:', err)
      setResult({ kind: 'error', message: describeUnexpectedError(err) })
    }
  }

  return (
    <section style={style.box}>
      <div style={style.brand}>
        <Logo size={28} />
        <h2 style={style.h2}>Sign in</h2>
      </div>
      {linkExpired && (
        <p style={style.error}>
          That sign-in link is no longer valid (already used or expired). Request a new one below.
        </p>
      )}
      <p style={style.hint}>Enter your account email and get a one-click sign-in link.</p>
      <form onSubmit={submit} style={style.form}>
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            clearWhenSettled(result, setResult, { kind: 'idle' })
            if (linkExpired) setLinkExpired(false)
          }}
          placeholder="hi@example.com"
          style={inputStyle}
        />
        <button type="submit" disabled={result.kind === 'submitting'} style={primaryButtonStyle}>
          {result.kind === 'submitting' ? 'Sending…' : 'Email sign-in link'}
        </button>
      </form>
      {result.kind === 'sent' && (
        <p style={style.success}>
          Sign-in link sent to <strong>{result.email}</strong>. Check your inbox.
        </p>
      )}
      {result.kind === 'unknown' && (
        <p style={style.error}>
          We don't have a Vike customer with the email <strong>{result.email}</strong>. Check for typos or contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>
            {SUPPORT_EMAIL}
          </a>
          . If you haven't bought a license, see{' '}
          <a href="https://vike.dev/pricing" style={linkStyle}>
            vike.dev/pricing
          </a>
          .
        </p>
      )}
      {result.kind === 'rateLimited' && <p style={style.error}>{RATE_LIMITED_MESSAGE}</p>}
      {result.kind === 'error' && <p style={style.error}>{result.message}</p>}
      <Footer />
    </section>
  )
}

const style = {
  box: {
    fontFamily: 'system-ui, sans-serif',
    maxWidth: 480,
    // Center the card in the viewport with breathing room on top — previously it rendered
    // flush in the top-left corner (it's the one branch not wrapped in +Page's centered <main>).
    margin: '48px auto',
    padding: 24,
    border: '1px solid #e5e5e5',
    // Thin Vike-brand accent along the card's top edge.
    borderTop: '3px solid #82AEC0',
    borderRadius: 8,
    background: '#fafafa',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  h2: { margin: 0 },
  hint: { ...hintStyle, margin: '8px 0 16px' },
  form: { display: 'grid', gap: 8 },
  success: successStyle,
  error: errorStyle,
}
